import React, {useState} from 'react';
import {Button, message, Modal} from 'antd';
import {PlusOutlined} from '@ant-design/icons';
import {DirectoryTree} from '../DirectoryTree';
import {NameModal} from '../NameModal';
import {directoryService} from '../../services/directory';
import './index.scss';

interface MoveModalProps {
  open: boolean;
  onCancel: () => void;
  onOk: (target: {
    id: number | string;
    level: number | null;
  }) => Promise<void>;
  rootId?: number;
}

export function MoveModal({open, onCancel, onOk, rootId = 1}: MoveModalProps) {
  const [targetId, setTargetId] = useState<number | string | null>(null);
  const [targetName, setTargetName] = useState<string>('');
  const [targetLevel, setTargetLevel] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [treeRefreshKey, setTreeRefreshKey] = useState(0);

  async function handleOk() {
    if (!targetId) {
      void message.warning('请选择目标目录');
      return;
    }

    setLoading(true);

    try {
      await onOk({
        id: targetId,
        level: targetLevel
      });
      reset();
    } catch (e) {
      void message.error('移动失败');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateFolder(name: string) {
    if (!name.trim()) return;

    const parentId = targetId || rootId;

    try {
      const res = await directoryService.create({
        parent_id: parentId,
        name,
      });

      setTreeRefreshKey(prev => prev + 1);

      const newId = res?.data || res;
      if (newId) {
        setTargetId(newId);
        setTargetName(name);
        setTargetLevel((targetLevel ?? 0) + 1);
      }

      setCreateOpen(false);
      void message.success('新建文件夹成功');
    } catch (e) {
      void message.error('新建文件夹失败');
    }
  }

  function reset() {
    setTargetId(null);
    setTargetName('');
    setTargetLevel(null);
    setLoading(false);
    setCreateOpen(false);
  }

  return (
    <>
      <Modal
        className="drive-move-modal"
        title="移动到"
        visible={open}
        confirmLoading={loading}
        okText="移动到这里"
        cancelText="取消"
        onOk={handleOk}
        onCancel={onCancel}
        afterClose={reset}
        destroyOnClose
      >
        <div className="drive-move-modal__header">
          <div className="drive-move-modal__tip">
            请选择要移动到的目标文件
          </div>

          <Button
            size="small"
            icon={<PlusOutlined/>}
            onClick={() => setCreateOpen(true)}
          >
            新建文件夹
          </Button>
        </div>

        <div className="drive-move-modal__tree">
          <DirectoryTree
            mode="picker"
            source="mixed"
            rootId={rootId}
            currentId={targetId ?? undefined}
            refreshKey={treeRefreshKey}
            refreshParentId={targetId || rootId}
            onSelect={(node: any) => {
              setTargetId(node.id);
              setTargetName(node.name);
              setTargetLevel(node.level);
            }}
          />
        </div>

        <div className="drive-move-modal__target">
          目标位置：
          <span>{targetName || '未选择'}</span>
        </div>
      </Modal>

      <NameModal
        open={createOpen}
        title="新建文件夹"
        placeholder="请输入文件夹名称"
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreateFolder}
      />
    </>
  );
}
